import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { type SubscriptionDTO } from "@shared/types/common";
import { analyticsService } from "@shared/services/analytics";
import { jpdUtils } from "@shared/utils/jpd-utils";
import { appcuesService } from "@shared/services/appcues";
import { navigationService } from "@shared/services/navigation.ts";

export const AllSubscriptionsSelectLabel = "All Subscriptions";

export const useSubscriptionsStore = defineStore("subscriptions", () => {
  // State
  const _subscriptions = ref<SubscriptionDTO[] | null>(null);
  const _currentSubscriptionNumber = ref<string>("");

  // Helper functions
  const getSubscriptionHelper = (): SubscriptionDTO | null => {
    if (_subscriptions.value === null) {
      return null;
    }
    const subscription = _subscriptions.value.find(
      (currSubscription) =>
        currSubscription.accountNumber.toString() ===
        _currentSubscriptionNumber.value,
    );
    if (subscription === undefined) {
      return null;
    }
    return subscription;
  };

  const hasCurrentSubscriptionHelper = (): boolean => {
    return (
      _currentSubscriptionNumber.value !== "" &&
      _currentSubscriptionNumber.value !== AllSubscriptionsSelectLabel
    );
  };

  // Getters
  const subscriptions = computed(() => _subscriptions.value);
  const saasSubscriptions = computed(
    () =>
      _subscriptions.value?.filter(
        (subscription) => subscription.meta.isSaas,
      ) || [],
  );
  const selfHostedSubscriptions = computed(
    () =>
      _subscriptions.value?.filter(
        (subscription) => subscription.meta.isSelfHosted,
      ) || [],
  );
  const currentSubscription = computed(() => getSubscriptionHelper());
  const isCurrentSubscriptionActive = computed(
    () => getSubscriptionHelper()?.state === "ACTIVE",
  );
  const currentSubscriptionNumber = computed(
    () => _currentSubscriptionNumber.value,
  );
  const hasCurrentSubscription = computed(() => hasCurrentSubscriptionHelper());
  const isMultiJpdsAccessible = computed(() => {
    const currentSub = getSubscriptionHelper();
    if (!currentSub) {
      return false;
    }
    const accessibleJpdItems = currentSub.jpus.filter(
      (jpdItem) => !jpdUtils.isJpdNotAccessible(jpdItem),
    );
    return accessibleJpdItems.length > 1;
  });
  const isMultiPlatformsAccessible = computed(() => {
    const currentSub = getSubscriptionHelper();
    if (!currentSub) {
      return false;
    }
    const accessiblePlatforms = currentSub.jpus.filter(
      (jpdItem) =>
        !jpdUtils.isJpdNotAccessible(jpdItem) &&
        jpdUtils.isPlatformJpd(jpdItem),
    );
    return accessiblePlatforms.length > 1;
  });
  const isFreeOrProSubscription = computed(() => {
    const currentSub = getSubscriptionHelper();
    return (
      currentSub?.meta.isFree ||
      currentSub?.meta.isProTeam ||
      currentSub?.meta.isCloudPro ||
      currentSub?.meta.isCloudProX
    );
  });
  const currentSubscriptionNumberInt = computed(() =>
    parseInt(_currentSubscriptionNumber.value),
  );
  const primaryAdmin = computed(() => {
    return hasCurrentSubscriptionHelper()
      ? getSubscriptionHelper()?.owners.find(
          (owner) => owner.role === "MYJFROG_PRIMARY_ADMIN",
        )
      : null;
  });

  // Actions
  const setSubscriptions = async (subs: SubscriptionDTO[] | null) => {
    _subscriptions.value = subs;
  };

  const setCurrentSubscription = (subscriptionNumber: string | number) => {
    _currentSubscriptionNumber.value = subscriptionNumber.toString();
    analyticsService.setSubscriptionProperties();
    appcuesService.setSubscriptionProperties();
    jpdUtils.setDefaultCurrentJpds();
  };

  const clearSubscriptions = () => {
    _subscriptions.value = null;
    _currentSubscriptionNumber.value = "";
  };

  const updateExistingSubscription = (newSubscription: SubscriptionDTO) => {
    if (!_subscriptions.value) {
      return;
    }
    const indexOfSubscriptionToUpdate = _subscriptions.value.findIndex(
      (subscription) =>
        subscription.accountNumber === newSubscription.accountNumber,
    );
    if (indexOfSubscriptionToUpdate === -1) {
      return;
    }
    _subscriptions.value[indexOfSubscriptionToUpdate] = newSubscription;
    navigationService.manageMenuItemsOnSubscriptionChanged(
      newSubscription.accountNumber + "",
    );
  };

  return {
    subscriptions,
    saasSubscriptions,
    selfHostedSubscriptions,
    currentSubscription,
    isCurrentSubscriptionActive,
    currentSubscriptionNumber,
    hasCurrentSubscription,
    isMultiJpdsAccessible,
    isMultiPlatformsAccessible,
    isFreeOrProSubscription,
    currentSubscriptionNumberInt,
    primaryAdmin,
    setSubscriptions,
    setCurrentSubscription,
    clearSubscriptions,
    updateExistingSubscription,
  };
});
